import MenuItem from '@mui/material/MenuItem';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import makeStyles from '@mui/styles/makeStyles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PageviewOutlinedIcon from '@mui/icons-material/PageviewOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import clsx from 'clsx';
import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { GET_SIGNED_URL_DOWNLOAD_COMPONENT, GET_SIGNED_URL_DOWNLOAD_EXTRACTED_COMPONENT } from '../../graphql/queries/asset';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import NRAssetsActionsStyles from './NRAssetsActions.styles';
import useNRLazyQuery from '../../graphql/useNRLazyQuery';
import NRAnalysisLog from '../../components/NRAnalysisLog/NRAnalysisLog';
import CustomError from '../../components/CustomError/CustomError';
import { AssetsContext } from '../../DataShare/NRAssetsDataShare';
import { NRMenu } from '../../components/NRMenu/NRMenu';
import { CONSTANTS } from '../../constants/constants';
import WithPermissions from '../../components/Permissions/WithPermissions';
import NRGroupIcon from '../../NRIcons/NRGroupIcon';
import * as React from 'react';
import { AssetGroupContext } from '../../components/AssetGroup/assetGroupContextWrapper';
import Typography from '@mui/material/Typography';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import Divider from '@mui/material/Divider';
import { useLocation } from 'react-router-dom';
import { removeTabAndSubTabFromFilePath } from '../../services/urlInterceptor.utils';
import RemoveFromGroupModal from '../../components/AssetGroup/RemoveFromGroupModal/RemoveFromGroupModal';
import { encodeParam } from '../../services/params.utils';
import { useGeneratePDF } from '../../pages/NRReport/pdfGenerator';
import { createRiseAIPdfTaskParams } from '../../utils/riseAiPdfTask';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../constants/constants';
import NRActionModal from '../../components/NRActionModal/NRActionModal';
import PDFReportSelectionModal from '../../components/PDFReportSelectionModal/PDFReportSelectionModal';
import { GET_RISE_AI_AVAILABILITY } from '../../graphql/queries/riseAI';
import { RISE_AI_STATUS } from '../../components/NRRiseAiChat/constants';
const useStyles = makeStyles(NRAssetsActionsStyles);

function NRAssetsActions({ component, anchorEl, setAnchorEl, onMenuClose, handleEditClick, assetGroupId }) {
  const [analysisLogModalJobID, setAnalysisLogModalJobID] = useState(null);
  const [downloadTask, setDownloadTask] = useState(null);
  const [pdfReportModalOpen, setPdfReportModalOpen] = useState(false);
  const [isAssetSummarySelected, setIsAssetSummarySelected] = useState(false);
  const [isPQCReadinessSelected, setIsPQCReadinessSelected] = useState(false);
  const [isRiseAISelected, setIsRiseAISelected] = useState(false);
  const open = Boolean(anchorEl);
  const classes = useStyles();
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [componentId, setComponentId] = useState(null);
  const effectiveComponent = component ?? selectedComponent;
  const { enqueueSnackbar } = useSnackbar();
  const { tasks, setTasks } = useContext(AssetsContext);
  const { setShowManageGroupsModal, setGroupsModalContent, setSelectedAsset, setDisableEdit } = useContext(AssetGroupContext);
  const { handleGeneratePDF, isGenerating } = useGeneratePDF();
  const location = useLocation();
  const disableActions = effectiveComponent?.row?.status === CONSTANTS.TABLE.ASSETS.STATUS.IN_PROGRESS;
  const { value: isRiseAIInsightsReportEnabled } = useFlag(FEATURE_FLAGS.RISE_AI_INSIGHTS_REPORT_SETTINGS, false);
  const { value: isPQCReadinessReportEnabled } = useFlag(FEATURE_FLAGS.PQC_READINESS_REPORT, false);

  const [caasAvailability, setCaasAvailability] = useState(false);
  const previousComponentIdRef = useRef(null);
  const [caasAvailabilityData, caasAvailabilityLoading, caasCalled, makeCaaSAvailabilityRequest] = useNRLazyQuery({
    query: GET_RISE_AI_AVAILABILITY,
    options: {
      context: { version: 'v3' },
      fetchPolicy: 'no-cache'
    },
    qlObjKey: 'riseAIAvailability',
    defaultValue: {
      available: false,
      eligible: false,
      status: RISE_AI_STATUS.UNSPECIFIED
    }
  });

  useEffect(() => {
    if (component) {
      setSelectedComponent(component);
      setComponentId(component?.id || component?.componentId);
    }
  }, [component]);

  useEffect(() => {
    if (previousComponentIdRef.current !== componentId) {
      previousComponentIdRef.current = componentId;
      setCaasAvailability(false);

      if (isRiseAIInsightsReportEnabled && componentId) {
        const requestedComponentId = componentId;
        makeCaaSAvailabilityRequest({
          variables: {
            args: {
              assetId: componentId
            }
          },
          context: { version: 'v3' },
          onCompleted: data => {
            if (requestedComponentId !== previousComponentIdRef.current) {
              return;
            }
            setCaasAvailability(data?.riseAIAvailability?.available ?? false);
          }
        });
      }
    }
  }, [componentId, isRiseAIInsightsReportEnabled, component, makeCaaSAvailabilityRequest]);

  const handleMenuClose = () => {
    setAnchorEl(null);
    !!onMenuClose && onMenuClose();
  };

  const handlePivotToSearch = () => {
    handleMenuClose();
    let extraParams = new URLSearchParams();
    extraParams.append('asset', encodeParam([componentId]));
    window.open(`/search/Search?${extraParams.toString()}`, '_blank');
  };

  const [assetDownload, isAssetDownloadLoading, , getAssetDownloadOperationId] = useNRLazyQuery({
    query: GET_SIGNED_URL_DOWNLOAD_COMPONENT,
    qlObjKey: 'download',
    options: { fetchPolicy: 'no-cache', context: { version: 'v3' } },
    defaultValue: '',
    transformData: data => {
      return data?.firmware?.downloadUrlsList;
    }
  });

  const [extractedAssetDownload, isExtractedAssetDownloadLoading, , getExtractedAssetDownloadOperationId] = useNRLazyQuery({
    query: GET_SIGNED_URL_DOWNLOAD_EXTRACTED_COMPONENT,
    qlObjKey: 'download',
    options: { fetchPolicy: 'no-cache', context: { version: 'v3' } },
    defaultValue: '',
    transformData: data => {
      return data?.extractedFirmware?.downloadUrlsList;
    }
  });

  function handleViewAnalysisLogClick() {
    !!componentId && setAnalysisLogModalJobID(componentId);
    handleMenuClose();
  }

  function handlePdfGeneration() {
    if (isRiseAIInsightsReportEnabled || isPQCReadinessReportEnabled) {
      handleMenuClose();
      setIsAssetSummarySelected(false);
      setIsPQCReadinessSelected(false);
      setIsRiseAISelected(false);
      setPdfReportModalOpen(true);
    } else {
      // Keep original behavior: don't close menu until PDF generation finishes
      !!componentId && handleGeneratePDF(componentId, handleMenuClose);
    }
  }

  const handleAssetSummaryToggle = () => {
    setIsAssetSummarySelected(prev => !prev);
  };

  const handlePQCReadinessToggle = () => {
    if (!isPQCReadinessReportEnabled) {
      return;
    }
    setIsPQCReadinessSelected(prev => !prev);
  };

  const handleRiseAIToggle = () => {
    setIsRiseAISelected(prev => !prev);
  };

  const handlePdfReportModalClose = () => {
    setPdfReportModalOpen(false);
  };

  const [shouldCloseModalAfterGeneration, setShouldCloseModalAfterGeneration] = useState(false);

  const handlePdfReportGenerate = useCallback(async () => {
    if (!componentId) {
      enqueueSnackbar('Unable to generate PDF: Asset ID is missing.', {
        variant: 'error'
      });
      return;
    }

    const assetName = effectiveComponent?.row?.name || effectiveComponent?.name || effectiveComponent?.component?.name || 'asset';
    const formattedAssetName = assetName.replace(/\s+/g, '_');

    try {
      if (isAssetSummarySelected) {
        handleGeneratePDF(componentId);
        setShouldCloseModalAfterGeneration(true);
      }

      if (isPQCReadinessSelected && isPQCReadinessReportEnabled) {
        const taskParams = {
          id: uuidv4(),
          type: CONSTANTS.TASKS.TYPE.DOWNLOAD_HTTP_POST,
          status: CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.CREATED,
          fileName: `${formattedAssetName}-PQC-Audit-Report.pdf`,
          downloadUrl: 'generate-pdf/pqc',
          body: {
            assetId: componentId,
            baseUrl: window.location.origin,
            pathname: removeTabAndSubTabFromFilePath(location)
          },
          headers: {},
          onSuccess: () => {
            if (!isAssetSummarySelected && !isRiseAISelected) {
              handlePdfReportModalClose();
            }
          },
          onError: () => {},
          errorMessage: 'Failed to generate PQC Readiness PDF. Please try again.'
        };
        setTasks(prev => [taskParams, ...prev]);
        if (!isAssetSummarySelected && !isRiseAISelected) {
          handlePdfReportModalClose();
        }
      }

      if (isRiseAISelected && isRiseAIInsightsReportEnabled) {
        const taskParams = createRiseAIPdfTaskParams(componentId, formattedAssetName, {
          onSuccess: () => {
            if (!isAssetSummarySelected && !isPQCReadinessSelected) {
              handlePdfReportModalClose();
            }
          }
        });
        setTasks(prev => [taskParams, ...prev]);
        if (!isAssetSummarySelected) {
          handlePdfReportModalClose();
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate PDF. Please try again later.', {
        variant: 'error'
      });
      if (!isAssetSummarySelected) {
        handlePdfReportModalClose();
      }
    }
  }, [
    componentId,
    effectiveComponent,
    isAssetSummarySelected,
    isPQCReadinessSelected,
    isPQCReadinessReportEnabled,
    isRiseAISelected,
    isRiseAIInsightsReportEnabled,
    handleGeneratePDF,
    enqueueSnackbar,
    setTasks,
    handlePdfReportModalClose
  ]);

  useEffect(() => {
    if (shouldCloseModalAfterGeneration && !isGenerating) {
      setShouldCloseModalAfterGeneration(false);
      handlePdfReportModalClose();
    }
  }, [shouldCloseModalAfterGeneration, isGenerating, handlePdfReportModalClose]);

  const hasSelectedReports = isAssetSummarySelected || isRiseAISelected || (isPQCReadinessSelected && isPQCReadinessReportEnabled);
  const mightHaveRiseAI = isRiseAIInsightsReportEnabled && (caasAvailabilityLoading || caasAvailability);
  const availableReportsCount = 1 + (isPQCReadinessReportEnabled ? 1 : 0) + (mightHaveRiseAI ? 1 : 0);
  const pdfReportText = `Generate PDF Report${availableReportsCount > 1 ? 's' : ''}`;

  function handleAnalysisLogModalClose() {
    setAnalysisLogModalJobID(null);
  }

  const downloadComponent = () => {
    if (isAssetDownloadLoading) return;
    getAssetDownloadOperationId({ variables: { args: { assetId: componentId } } });
  };

  const downloadExtractedComponent = () => {
    if (isExtractedAssetDownloadLoading) return;
    getExtractedAssetDownloadOperationId({ variables: { args: { assetId: componentId } } });
  };

  useEffect(() => {
    if (assetDownload) {
      setDownloadTask({ downloadUrl: assetDownload, isExtracted: false });
    }
  }, [assetDownload]);

  useEffect(() => {
    if (extractedAssetDownload) {
      setDownloadTask({ downloadUrl: extractedAssetDownload, isExtracted: true });
    }
  }, [extractedAssetDownload]);

  const handleManageGroupsClick = () => {
    setSelectedAsset(effectiveComponent?.row || effectiveComponent);
    setDisableEdit(false);
    setShowManageGroupsModal(true);
    handleMenuClose();
  };

  const handleDeleteGroupsClick = () => {
    setGroupsModalContent({ content: <RemoveFromGroupModal assetId={componentId} groupId={assetGroupId} />, title: 'Remove from Group' });
    handleMenuClose();
  };

  useEffect(() => {
    async function asyncFun() {
      if (!!downloadTask?.downloadUrl) {
        if (tasks.find(e => e.downloadUrl === downloadTask.downloadUrl && e.status === CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.IN_PROGRESS)) {
          enqueueSnackbar(<CustomError message={CONSTANTS.DOWNLOAD.ALREADY_IN_PROGRESS_MESSAGE} />, {
            variant: 'warning',
            preventDuplicate: true
          });
          return;
        }
        setTasks(prev => [
          {
            downloadUrl: downloadTask.downloadUrl,
            fileName: `${effectiveComponent?.name || effectiveComponent?.component?.name}${downloadTask.isExtracted ? '_extracted.tar.gz' : ''}`,
            status: CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS.CREATED,
            type: CONSTANTS.TASKS.TYPE.DOWNLOAD,
            id: uuidv4()
          },
          ...prev
        ]);
      }
    }
    asyncFun();
  }, [downloadTask]);

  return (
    <div>
      <NRMenu
        id="demo-customized-menu"
        MenuListProps={{
          'aria-labelledby': 'actions-button'
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
      >
        {!!handleEditClick && (
          <WithPermissions permission={CONSTANTS.PERMISSIONS.WRITE_IMGS}>
            <MenuItem disabled={disableActions} onClick={handleEditClick} disableRipple className={clsx(classes.item, classes.firstItem)}>
              <ModeEditOutlineOutlinedIcon />
              Edit Asset Details
            </MenuItem>
          </WithPermissions>
        )}
        <MenuItem
          disabled={disableActions}
          onClick={handleManageGroupsClick}
          disableRipple
          className={clsx(classes.item, classes.groupsIcon, { [classes.firstItem]: !handleEditClick })}
        >
          <NRGroupIcon width={'24'} height={'24'} />
          <Typography sx={{ pl: 1.5 }}>Manage Groups</Typography>
        </MenuItem>
        <MenuItem onClick={handleViewAnalysisLogClick} disableRipple className={classes.item}>
          <ArticleOutlinedIcon />
          View Analysis Log
        </MenuItem>
        <MenuItem disabled={disableActions} onClick={handlePdfGeneration} disableRipple className={classes.item}>
          {/* <DescriptionOutlinedIcon /> */}
          {!isGenerating && <DescriptionOutlinedIcon />}
          {isGenerating && <CircularProgress className={classes.downloadLoading} size={20} />}
          {pdfReportText}
        </MenuItem>
        {/* To be uncommented when the functionality si developed. */}
        <MenuItem disabled={disableActions} onClick={() => downloadComponent()} disableRipple className={classes.item}>
          {!isAssetDownloadLoading && <FileDownloadOutlinedIcon />}
          {isAssetDownloadLoading && <CircularProgress className={classes.downloadLoading} size={20} />}
          Download Asset
        </MenuItem>
        <MenuItem disabled={disableActions} onClick={() => downloadExtractedComponent()} disableRipple className={classes.item}>
          {!isExtractedAssetDownloadLoading && <FileDownloadOutlinedIcon />}
          {isExtractedAssetDownloadLoading && <CircularProgress className={classes.downloadLoading} size={20} />}
          Download Extracted Asset
        </MenuItem>
        {!handleEditClick && (
          <MenuItem disabled={disableActions} onClick={handlePivotToSearch} disableRipple className={classes.item}>
            <PageviewOutlinedIcon />
            Pivot To Search
          </MenuItem>
        )}
        <WithPermissions permission={CONSTANTS.PERMISSIONS.DELETE_GROUPS}>
          <>
            {location?.pathname === '/groups' && <Divider sx={{ my: 0.5 }} />}
            {location?.pathname === '/groups' && (
              <MenuItem disabled={disableActions} onClick={handleDeleteGroupsClick} disableRipple className={classes.item}>
                <HighlightOffOutlinedIcon />
                Remove From Group
              </MenuItem>
            )}
          </>
        </WithPermissions>
        {/* <Divider sx={{ my: 0.5 }} /> */}
        {/* <MenuItem onClick={handleMenuClose} disableRipple className={clsx(classes.item, classes.lastItem)}>
              <DeleteOutlinedIcon />
              Delete Asset
            </MenuItem> */}
      </NRMenu>
      <NRAnalysisLog handleClose={handleAnalysisLogModalClose} open={!!analysisLogModalJobID} componentId={analysisLogModalJobID} />
      <NRActionModal
        title={pdfReportText}
        content={
          <PDFReportSelectionModal
            isAssetSummarySelected={isAssetSummarySelected}
            isPQCReadinessSelected={isPQCReadinessSelected}
            isRiseAISelected={isRiseAISelected}
            onAssetSummaryToggle={handleAssetSummaryToggle}
            onPQCReadinessToggle={handlePQCReadinessToggle}
            onRiseAIToggle={handleRiseAIToggle}
            isGenerating={isGenerating}
            isRiseAIInsightsReportEnabled={isRiseAIInsightsReportEnabled && caasAvailability}
            isPQCReadinessReportEnabled={isPQCReadinessReportEnabled}
            onGenerate={handlePdfReportGenerate}
            onCancel={handlePdfReportModalClose}
            hasSelectedReports={hasSelectedReports}
          />
        }
        open={pdfReportModalOpen}
        handleClose={handlePdfReportModalClose}
        width={500}
      />
    </div>
  );
}
export default NRAssetsActions;
